import { useEffect, useRef, useState } from 'react';
import type { UserSummary } from '@/types/user';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { TimezoneSelect } from '@/components/shell/TimezoneSelect';

interface UserMenuProps {
  user: UserSummary;
  onLogout: () => void;
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 hover:bg-[var(--bg-card-hover)]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--accent-bg)] text-sm font-semibold text-[var(--accent-light)]">
          {user.displayName.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden text-sm md:inline">{user.displayName}</span>
        <ChevronDownIcon className="h-4 w-4 text-secondary" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-64 rounded-lg border border-[var(--border-visible)] bg-[var(--bg-secondary)] p-1 shadow-lg"
        >
          <div className="px-3 py-2 text-xs text-secondary">{user.portal === 'admin' ? 'Admin' : 'Tenant'} portal</div>
          <div className="px-2 py-1 sm:hidden">
            <TimezoneSelect />
          </div>
          <button
            type="button"
            role="menuitem"
            className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--bg-card-hover)]"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}
